/* eslint-disable @typescript-eslint/no-restricted-imports */
import classnames from 'classnames';
import { ReactNode } from 'react';

import { useBooleanFlags } from '@/domains/core/flags/flags.hooks';
import { getDomains } from '@/domains/core/platform/domains/getDomains';
import { getCurrentBaseUrl } from '@/domains/core/platform/getCurrentBaseUrl';
import { Link } from '@/domains/core/routing/components/Link';
import { routes } from '@/domains/core/routing/routes/routes.gb';
import { createURL } from '@/domains/core/routing/URLParser';
import { isFrancePlatform } from '@/domains/core/settings/utils';
import {
  P,
  Span,
} from '@/domains/core/tamagoshiTailwind/components/Typography/Typography';
import { ArrowNextIcon } from '@/domains/core/tamagoshiv2/icons/system/ArrowNextIcon';
import { GiftIcon } from '@/domains/core/tamagoshiv2/icons/system/GiftIcon';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import { ABTests } from '@/domains/proB2BAnimation/abtests';
import { useGetProRegisterRoute } from '@/domains/proB2BAnimation/hooks/useGetProRegisterRoute';
import { useWelcomeOffer } from '@/domains/proB2BAnimation/hooks/useWelcomeOffer';
import { cta as createProAccount } from '@/domains/proB2BAnimation/modules/ProModal/ProModalList/translations';
import { modalTitle as doYouBuyForYourCompany } from '@/domains/proB2BAnimation/modules/ProModal/ProModalWelcomeCoupon/translations';
import { useB2bPriceAdvantage } from '@/domains/productDiscovery/ProductPage/b2b/hooks/useB2bPriceAdvantage';
import {
  b2bAdvantageTextV2,
  defaultText,
  deferredPayment,
  newDefaultText,
  proWelcomeOfferDiscount,
  proWelcomeOfferlabel,
  proWelcomeOfferlabelMultiCoupon,
} from '@/domains/productDiscovery/ProductPage/b2c/components/ProBlock/translations';
import { useDetail } from '@/domains/productDiscovery/ProductPage/hooks/useDetail';
import { articlePathToSlugId } from '@/domains/productDiscovery/ProductPage/utils/productUrl/articlePathToSlugId';

import styles from './ProPriceAndWelcomeOffer.module.scss';

const MIN_PERCENT_ADVANTAGE = 2;

export const ProPriceAndWelcomeOffer = () => {
  const {
    amount: couponAmount,
    isExpired,
    isOfferMultiCoupon,
  } = useWelcomeOffer();

  const [enablePdpBnpl] = useBooleanFlags([ABTests.PEPE_PDP_B2B_BNPL]);

  const trackEvent = () => {
    Gtm.push({
      event: 'interaction_click on login b2b',
      e_category: 'account', // Event category
      e_action: 'login b2b',
      event_trigger: 'Product_page CTA',
      amp_event_name: 'Click on login b2b',
    });
  };

  const detail = useDetail<'b2c'>();
  const b2bPriceAdvantage = useB2bPriceAdvantage(detail?.modelId) ?? 0;
  const hasB2bPriceAdvantage = b2bPriceAdvantage >= MIN_PERCENT_ADVANTAGE;

  const { domainPro } = getDomains();
  const b2bProductUrl = `${getCurrentBaseUrl(domainPro)}${createURL(
    routes.product,
    articlePathToSlugId(detail.articlePath),
  )}`;

  const b2bSignupUrl = useGetProRegisterRoute(b2bProductUrl);

  const noAdvantages = !hasB2bPriceAdvantage && (!couponAmount || isExpired);

  return (
    <Link
      href={b2bSignupUrl}
      onClick={trackEvent}
      className={classnames(styles.blockContainer, 'b2b')}
    >
      <div>
        <P weight="bold" scale="body2" className={styles.discoverLabel}>
          {doYouBuyForYourCompany}
        </P>

        {hasB2bPriceAdvantage && (
          <B2BPriceAdvantage
            b2bPriceAdvantage={b2bPriceAdvantage}
            enablePdpBnpl={enablePdpBnpl}
          />
        )}

        {couponAmount && !isExpired && (
          <WelcomeOfferAdvantage
            isOfferMultiCoupon={isOfferMultiCoupon}
            couponAmount={couponAmount}
          />
        )}

        {noAdvantages && <DefaultAdvantage enablePdpBnpl={enablePdpBnpl} />}
      </div>

      <div role="presentation" className={styles.ctaContainer}>
        <P scale="body2" weight="semibold" className={styles.ctaText}>
          {createProAccount}
        </P>
        <ArrowNextIcon />
      </div>
    </Link>
  );
};

const B2BPriceAdvantage: React.FC<{
  b2bPriceAdvantage: number;
  enablePdpBnpl: boolean;
}> = ({ b2bPriceAdvantage, enablePdpBnpl }) => (
  <>
    <P scale="body2">
      {b2bAdvantageTextV2.format({
        percentage: (
          <Span weight="bold" scale="body2" key={'pro-b2b-advantage-amount'}>
            {`-${b2bPriceAdvantage}%`}
          </Span>
        ),
      })}
    </P>
    {enablePdpBnpl && isFrancePlatform() && (
      <P scale="body2">{deferredPayment}</P>
    )}
  </>
);
const WelcomeOfferAdvantage = ({
  isOfferMultiCoupon,
  couponAmount,
}: {
  isOfferMultiCoupon: boolean | null;
  couponAmount: string;
}) => (
  <div className={styles.labelRow}>
    <GiftIcon className={styles.icon} />
    <P scale="body2">
      {(isOfferMultiCoupon
        ? proWelcomeOfferlabelMultiCoupon
        : proWelcomeOfferlabel
      ).format({
        span: (...chunks: ReactNode[]) => (
          <Span key="pro-block-discount" weight="bold" scale="body2">
            {chunks}
          </Span>
        ),
        discount: proWelcomeOfferDiscount.format({ couponAmount }),
      })}
    </P>
  </div>
);

const DefaultAdvantage: React.FC<{
  enablePdpBnpl: boolean;
}> = ({ enablePdpBnpl }) => (
  <>
    {!enablePdpBnpl && <P scale="body2">{defaultText}</P>}
    {enablePdpBnpl && isFrancePlatform() && (
      <>
        <P scale="body2">{newDefaultText}</P>
        <P scale="body2">{deferredPayment}</P>
      </>
    )}
  </>
);
